import './index.scss'
import channelTypes from '../../data/channelTypes.json'
import Footer from '../../components/layouts/footer'
import {
    AndroidIcon,
    CheckIcon,
    EditIcon,
    EnvelopIcon,
    HorizontalMoreIcon,
    IosIcon,
    MSIcon,
    PausedIcon,
    PhoneIcon,
    PlayIcon,
    PlusIcon,
    SendMessageIcon,
    SimpleDiscordIcon,
    SlackIcon,
    SmartphoneIcon,
    SmsIcon,
    TelegramIcon,
    TrashIcon,
    WarningIcon,
    WebhookIcon
} from '../../icons'
import {ImgButton, SmallButton} from '../../components/core/button'
import {PageSubTitle, PageTitle} from '../../components/core/title'
import {useEffect, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {
    addChannel,
    deleteChannels,
    getPersonalNotificationChannels,
    sendTestAlert,
    setCheckedChannels,
    setIsFetching,
    setPersonalChannels,
    updateChannelStatus
} from '../../stores/customer-settings'
import ResponsivePageTitle from '../../components/core/title/ResponsivePageTitle'
import ContentLoader from '../../components/core/loader'
import ButtonLoader from '../../components/core/button/ButtonLoader'
import CodePopup from './codePopup'
import InfoBox from '../../components/core/infoBox'
import Table from '../../components/core/table'
import AddNotificationChannel from './addForms/AddNotificationChannel'
import AddTeamChannel from './addForms/AddTeamChannel'
import UpdateSmsChannel from './updateForms/updateSmsChannel'
import UpdateEmailChannel from './updateForms/updateEmailChannel'
import UpdateWebhook from './updateForms/UpdateWebhook'
import Tooltip from '../../components/core/tooltip'
import {useLocation} from 'react-router-dom'
import TextHelper from '../../utils/text'
import {setConfirmIsActive} from '../../stores/alert'
import Confirm from '../../components/core/confirm'
import {toast} from 'react-toastify'
import EmptyContent from '../resources/EmptyContent'
import {routes} from '../../routes'
import Modal from '../../components/core/modal'
import LocalStorageManager, {getCurrentTest, getPreviousTestAlerts} from '../../utils/localStorageManager'
import localStorageKeys from '../../utils/localStorageKeys'
import LimitManager from '../../utils/limitManager'
import EmptyTeamChannels from './EmptyTeamChannels'
import Dropdown from '../../components/core/dropdown'
import {MenuDivider, MenuItem} from '@szhsin/react-menu'
import TeamSwitcher from '../../components/pages/teams'
import CustomerSwitcher from '../../components/pages/teams/CustomerSwitcher'
import Customer from '../../utils/customer'
import {colors} from '../../utils/colors'
import IntegrationTeamMenu from '../../components/pages/IntegrationsTeamMenu'
import classNames from 'classnames'
import {getAllLimitUsages} from '../../stores/auth'
import {useNamespaceTranslations} from '../../utils/hooks/useNamespacesTranslations'
import {envConfig} from '../../utils/env'

const SORT_ID = 'notifications'

const {isReadOnlyModeEnabled} = envConfig

const customFieldLogic = text => ({
    enabled: (item) => item?.enabled ? text.enabled_status : text.disabled_status
})

const NotificationChannels = () => {
    const text = useNamespaceTranslations('notifications')
    const usageLimitsData = useNamespaceTranslations('usage-limits')
    const dispatch = useDispatch()
    const location = useLocation()
    const personalChannels = useSelector(state => state.customer_settings.personalChannels)
    const checkedChannels = useSelector(state => state.customer_settings.checkedChannels)
    const isDeleting = useSelector(state => state.customer_settings.isDeleting)
    const isFetching = useSelector(state => state.customer_settings.isFetching)
    const members = useSelector(state => state.members.members)
    const isDarkMode = useSelector(state => state.sidebar.isDarkMode)
    const [sortedData, setSortedData] = useState([])
    const [isProgressing, setIsProgressing] = useState({})
    const [modalData, setModalData] = useState({})
    const [newChannelPopupIsActive, setNewChannelPopupIsActive] = useState(false)
    const [verifiedInfo, setVerifiedInfo] = useState(false)
    const [webhookChannelPopupIsActive, setWebhookChannelPopupIsActive] = useState(false)
    const [isDeleteable, setIsDeleteable] = useState(true)
    const [checkedTypes, setCheckedTypes] = useState([])
    const [checkingVerify, setCheckingVerify] = useState(null)
    const [verifiedValue, setVerifiedValue] = useState(null)
    const checkedChannel = personalChannels?.find(item => item.priority === checkedChannels[0])
    const [clickedChannel, setClickedChannel] = useState(null)
    const isTeamChannel = routes.team_channels.isSame(location.pathname)
    const teamId = Customer.isRoot() ?
        useSelector(state => state.teams.selectedTeamId) :
        LocalStorageManager.get(localStorageKeys.TEAM).team_id
    const [customerId, setCustomerId] = useState(!(Customer.isRoot() || Customer.isManager()) ? {value: Customer.getCurrentCustomerId()} : null)
    const customerIdString = customerId?.value
    const [type, setType] = useState(null)
    const tableHeader = [
        {
            label: 'Status',
            className: 'min-width-250',
            sorting: {
                asc: {
                    key: 'status_asc',
                    field: ['enabled']
                },
                desc: {
                    key: 'status_desc',
                    field: ['enabled']
                }
            }
        },
        {
            label: text.type,
            className: 'min-width-250',
            sorting: {
                asc: {
                    key: 'type_asc',
                    field: ['type']
                },
                desc: {
                    key: 'type_desc',
                    field: ['type']
                }
            }
        },
        {
            label: text.channel,
            className: 'min-width-220',
            sorting: {
                asc: {
                    key: 'channel_asc',
                    field: ['value', 'new_value', 'extra']
                },
                desc: {
                    key: 'channel_desc',
                    field: ['value', 'new_value', 'extra']
                }
            }
        },
        {
            label: '',
            className: 'flex-1 gap-1'
        }
    ]

    const tbodyData = []
    const notificationTypes = {
        'email': {
            'type': 'email',
            'text': text.email,
            'icon': <EnvelopIcon className="table-icon"/>
        },
        'push-notification': {
            'type': 'push-notification',
            'text': text.push_notification,
            'icon': <SmartphoneIcon className="table-icon"/>
        },
        'sms': {
            'type': 'sms',
            'text': text.sms,
            'icon': <SmsIcon className="table-icon"/>
        },
        'phone': {
            'type': 'phone',
            'text': text.phone,
            'icon': <PhoneIcon className="table-icon"/>
        },
        'webhook': {
            type: 'webhook',
            text: text.webhook_type,
            icon: <WebhookIcon className="filled-icon"/>
        },
        'slack': {
            type: 'slack',
            text: text.slack,
            icon: <SlackIcon className="table-icon"/>
        },
        'ms-teams': {
            type: 'ms-teams',
            text: text.ms_teams,
            icon: <MSIcon className="ms-icon table-icon"/>
        },
        'discord': {
            type: 'discord',
            text: text.discord,
            icon: <SimpleDiscordIcon className="table-icon"/>
        },
        'telegram': {
            type: 'telegram',
            text: text.telegram,
            icon: <TelegramIcon className="table-icon"/>
        }
    }

    const nonDeleteableTypes = ['email']

    const returnTooltipText = (value, type) => {
        switch (type) {
        case channelTypes.types.email:
            return text.has_verified_email.replace(':value', value)

        case channelTypes.types.sms:
            return text.has_verified_sms

        case channelTypes.types.phone:
            return text.has_verified_phone

        case channelTypes.types.webhook:
            return text.has_verified_webhook

        case channelTypes.types.telegram:
            return text.has_verified_telegram
        }
    }

    const returnChannelValue = item => {
        const isNew = item?.new_value
        if (item?.type === 'push-notification') {
            const extra = item?.extra
            return <span className="d-flex align-items-center gap-half">
                <span>
                    {extra?.platform === 'ios' ? <IosIcon width={20} height={20} className="phone-icon"/> :
                        <AndroidIcon width={20} height={20} className="phone-icon"/>}
                </span>
                {extra?.device}
            </span>
        }
        if (item?.type === channelTypes.types.slack) {
            const extra = item?.extra
            return <span className="d-flex align-items-center gap-half">
                {extra?.team_name}{extra?.channel}
            </span>
        }
        if (item?.type === channelTypes.types.discord) {
            const extra = item?.extra
            return <span className="d-flex align-items-center gap-half">
                {`${extra?.guild_name}@${extra?.channel_name}`}
            </span>
        }
        return <div className="d-flex flex-column gap-half">
            <span title={item?.new_value ? item?.new_value : item?.value}
                className="d-flex align-items-center email-value gap-half">
                {item?.new_value ? TextHelper.textLengthChecker(item?.new_value, 28) : TextHelper.textLengthChecker(item?.value, 28)}
                {isNew &&
                    <Tooltip content={<span dangerouslySetInnerHTML={{__html: returnTooltipText(item?.value, item?.type)}}/>}>
                        <WarningIcon className="warning-icon tooltip-target"/>
                    </Tooltip>
                }
            </span>
        </div>
    }

    const handleUpdateStatus = async(data) => {
        const localData = 'enabled' in data ? data : null
        const id = isTeamChannel ? teamId : customerIdString
        setIsProgressing({
            [checkedChannel?.enabled ? text.disabled_status : text.enabled_status]: true
        })

        await dispatch(updateChannelStatus(isTeamChannel, id, {
            enabled: localData ? !localData?.enabled : !checkedChannel?.enabled,
            priority: localData ? localData?.priority : checkedChannel?.priority
        }))
        dispatch(getPersonalNotificationChannels(isTeamChannel, false, customerIdString))
        setIsProgressing({})
        setClickedChannel(null)
    }

    const deleteChannel = () => {
        const channels = clickedChannel ? [clickedChannel?.priority] : checkedChannels
        const id = isTeamChannel ? teamId : customerIdString
        dispatch(deleteChannels(isTeamChannel, id, channels, customerIdString))
        setClickedChannel(null)
    }

    const handleNewChannelButtonOnclick = () => {
        setNewChannelPopupIsActive(!newChannelPopupIsActive)
    }

    const handleWebhookChannelButtonOnclick = () => {
        setWebhookChannelPopupIsActive(!webhookChannelPopupIsActive)
        setType(null)
    }

    const getEditButtonsStatus = channel => {
        const data = channel || checkedChannel
        const isEditable = (data?.type !== channelTypes.types.sms &&
            data?.type !== channelTypes.types.phone &&
            data?.type !== channelTypes.types.email &&
            data?.type !== channelTypes.types.webhook
        )
        return channel ? isEditable : (checkedChannels.length !== 1) || isEditable
    }

    const getEnableButtonsStatus = () => {
        return checkedChannels.length !== 1 || checkedChannel?.enabled
    }

    const getDisableButtonsStatus = () => {
        return checkedChannels.length !== 1 || !checkedChannel?.enabled
    }

    const handlePopup = item => {
        setModalData({
            status: true,
            channel: item
        })
    }

    const generateMobileAppLinks = () => {
        return <div className="mobile-app-links">
            <a className="play-store link" href="https://play.google.com/store/apps/details?id=com.pinghome"
                target="_blank"/>
            <a className="app-store link" href="https://apps.apple.com/app/pinghome/id1624903064" target="_blank"/>
        </div>
    }

    const getChannelStatus = channel => {
        return <span className="min-width-250">
            <span
                className={classNames('channel-status', {
                    'channel-enabled': !!channel?.enabled,
                    'channel-disabled': !channel?.enabled,
                    dark: isDarkMode
                })}>{channel?.enabled ? text.enabled_status : text.disabled_status}</span>
        </span>
    }

    const getChannelType = channel => {
        return <span className="min-width-250">
            <span className="type">
                {notificationTypes[channel?.type]?.icon}
                <span>{notificationTypes[channel?.type]?.text}</span>
            </span>
        </span>
    }

    const getChannelValue = channel => {
        return <span className="min-width-220 value">
            {returnChannelValue(channel)}
        </span>
    }

    const checkVerify = async(value) => {
        if (modalData?.type === channelTypes.types.telegram) return
        setCheckingVerify(value)
        await dispatch(getPersonalNotificationChannels(isTeamChannel, false, customerIdString))
        setCheckingVerify(null)
    }

    const getChannelVerifiedStatus = channel => {
        return <span className="flex-1 gap-1">
            {channel?.new_value ? (
                !isTeamChannel || channel?.type === channelTypes.types.telegram ? (
                    <span className="min-width flex-item">
                        <div className="button-container">
                            <SmallButton onClick={() => {
                                dispatch(setCheckedChannels([]))
                                setModalData({
                                    status: true,
                                    codeModal: true,
                                    channel: {
                                        priority: channel?.priority
                                    },
                                    value: channel?.new_value,
                                    type: channel?.type
                                })
                            }} className={classNames('verify', {
                                dark: isDarkMode
                            })}>{text.verify}
                            </SmallButton>
                        </div>
                    </span>
                ) : (
                    <span className="min-width flex-item">
                        <div className="button-container">
                            <SmallButton onClick={() => {
                                checkVerify(channel?.new_value)
                                setVerifiedInfo(true)
                                setVerifiedValue(channel?.priority)
                            }} className={classNames('verify', {
                                dark: isDarkMode
                            })}>
                                {checkingVerify === channel?.new_value && <ButtonLoader/>}
                                {text.refresh}
                            </SmallButton>
                        </div>
                    </span>
                )
            ) : (verifiedInfo && verifiedValue === channel?.priority) ?
                <div className="button-container">
                    <span className="verified-info"><CheckIcon/>{text.verified}</span>
                </div>
                : <div className="empty-column"/>}
            {getActionButton(channel)}
        </span>
    }

    const getActionButton = data => {
        return <Dropdown menuButton={<button className={classNames('table-action-btn', {
            'dark': isDarkMode
        })}><HorizontalMoreIcon/></button>}>
            <MenuItem disabled={getEditButtonsStatus(data) || isReadOnlyModeEnabled} onClick={() => {
                setClickedChannel(data)
                handlePopup(data)
            }
            }>
                <span className="d-flex"><EditIcon/></span>
                <span>{text.edit}</span>
            </MenuItem>
            <MenuItem disabled={getTestAlertStatus(data)} onClick={() => {
                sendAlert(data?.priority)
            }
            }>
                <span className="d-flex"><SendMessageIcon/></span>
                <span>{text.send_message}</span>
            </MenuItem>
            <MenuItem disabled={!data?.enabled || isReadOnlyModeEnabled} onClick={() => {
                handleUpdateStatus(data)
            }
            }>
                <span className="d-flex"><PausedIcon/></span>
                <span>{text.disable}</span>
            </MenuItem>
            <MenuItem disabled={data?.enabled || isReadOnlyModeEnabled} onClick={() => {
                handleUpdateStatus(data)
            }
            }>
                <span className="d-flex"><PlayIcon/></span>
                <span>{text.enable}</span>
            </MenuItem>
            <MenuDivider/>
            <MenuItem className="danger" onClick={() => {
                setClickedChannel(data)
                dispatch(setConfirmIsActive(true))
            }
            } disabled={isReadOnlyModeEnabled}>
                <span className="d-flex"><TrashIcon stroke={colors.colorRed}/></span>
                <span>{text.delete}</span>
            </MenuItem>
        </Dropdown>
    }

    const openDeleteChannelConfirm = () => {
        setClickedChannel(null)
        isDeleteable ? dispatch(setConfirmIsActive(true)) : toast.error(text.is_not_deleteable.replace(':types', nonDeleteableTypes.join(', ')))
    }

    const sendAlert = async(data) => {
        const priority = typeof data === 'number' ? data : null
        const id = isTeamChannel ? teamId : customerIdString
        setIsProgressing({
            test: true
        })
        await dispatch(sendTestAlert(isTeamChannel, id, {
            priority: priority || checkedChannel?.priority
        }))
        setIsProgressing(false)
    }

    const getTestAlertStatus = data => {
        const test = getCurrentTest(data || checkedChannel, isTeamChannel)
        return test?.count >= LimitManager.getTestAlertCount(usageLimitsData)
    }

    const openModalByType = localType => {
        setType(localType)
        setWebhookChannelPopupIsActive(true)
    }

    const getEmptyContent = () => {
        return isTeamChannel ? <EmptyTeamChannels openModal={openModalByType}/> :
            <EmptyContent message={text.no_channels} hideButton={true}/>
    }

    const generateChannels = () => {
        return (
            <>
                <IntegrationTeamMenu/>
                <div className="search-filter-actions">
                    <div className="selected-actions">
                        <ImgButton className={classNames({
                            'light-bg': !isDarkMode
                        })}
                        onClick={() => {
                            isTeamChannel ? handleWebhookChannelButtonOnclick() : handleNewChannelButtonOnclick()
                        }} disabled={isReadOnlyModeEnabled}>
                            <PlusIcon stroke={colors.colorMuted}/>
                            <span className="button-text">{text.new}</span>
                        </ImgButton>
                        <Tooltip content={text.test_alert_warning_msg} noButton>
                            <ImgButton className={classNames({
                                'light-bg': !isDarkMode
                            })}  onClick={sendAlert}
                            disabled={isProgressing?.test || getDisableButtonsStatus() || getTestAlertStatus()}>
                                {isProgressing?.test ? <ButtonLoader/> : <SendMessageIcon/>}
                                <span className="button-text">{text.send_message}</span>
                            </ImgButton>
                        </Tooltip>
                        <ImgButton className={classNames({
                            'light-bg': !isDarkMode
                        })}  disabled={isProgressing?.disabled || getDisableButtonsStatus() || isReadOnlyModeEnabled}
                        onClick={handleUpdateStatus}>
                            <span>
                                {isProgressing?.disabled ? <ButtonLoader/> : <PausedIcon/>}
                            </span>
                            <span className="button-text">{text.disable}</span>
                        </ImgButton>
                        <ImgButton className={classNames({
                            'light-bg': !isDarkMode
                        })}  disabled={isProgressing?.enabled || getEnableButtonsStatus() || isReadOnlyModeEnabled}
                        onClick={handleUpdateStatus}>
                            <span>
                                {isProgressing?.enabled ? <ButtonLoader/> : <PlayIcon/>}
                            </span>
                            <span className="button-text">{text.enable}</span>
                        </ImgButton>
                        <ImgButton className={classNames({
                            'light-bg': !isDarkMode
                        })}  disabled={getEditButtonsStatus() || isReadOnlyModeEnabled}
                        onClick={() => handlePopup(checkedChannel)}>
                            <EditIcon/>
                            <span className="button-text">{text.edit}</span>
                        </ImgButton>
                        <ImgButton className={classNames('outline-red', {
                            'light-bg': !isDarkMode
                        })}
                        disabled={isDeleting || checkedChannels?.length === 0 || isReadOnlyModeEnabled}
                        onClick={openDeleteChannelConfirm}>
                            <span><TrashIcon stroke={colors.colorRed}/></span>
                            <span className="button-text">{text.delete}</span>
                        </ImgButton>
                    </div>
                </div>
                {personalChannels?.length === 0 ?
                    getEmptyContent()
                    : (
                        <div className="content-body">
                            <Table
                                tableHeader={tableHeader}
                                tbodyData={tbodyData}
                                data={sortedData}
                                identificator='priority'
                                inputName='channel'
                                hasCheckbox={true}
                                isMultiCheckable={true}
                                selectedData={checkedChannels}
                                setSelectedData={data => dispatch(setCheckedChannels(data))}
                                isSortingEnabled
                                setData={setSortedData}
                                sortId={SORT_ID}
                                originalData={personalChannels}
                                customFieldLogic={customFieldLogic(text)}
                            />
                        </div>
                    )
                }
            </>
        )
    }

    sortedData?.map(channel => {
        tbodyData.push([
            getChannelStatus(channel),
            getChannelType(channel),
            getChannelValue(channel),
            getChannelVerifiedStatus(channel)
        ])
    })

    const closeUpdateWebhookPopup = () => {
        dispatch(getPersonalNotificationChannels(true, false))
        setModalData({})
    }

    const updateChannelPopups = {
        [channelTypes.types.email]: <Modal title={text.update_email_channel} closeModal={() => setModalData({})}
            isOpen={modalData?.status && !modalData?.codeModal}>
            <UpdateEmailChannel data={modalData} setModalData={setModalData} customerId={customerIdString}/>
        </Modal>,
        [channelTypes.types.sms]: <Modal title={text.update_sms_channel} closeModal={() => setModalData({})}
            isOpen={modalData?.status && !modalData?.codeModal}>
            <UpdateSmsChannel data={modalData} setModalData={setModalData} customerId={customerIdString} isDarkMode={isDarkMode}/>
        </Modal>,
        [channelTypes.types.phone]: <Modal title={text.update_phone_channel} closeModal={() => setModalData({})}
            isOpen={modalData?.status && !modalData?.codeModal}>
            <UpdateSmsChannel data={modalData} setModalData={setModalData} customerId={customerIdString} isDarkMode={isDarkMode}/>
        </Modal>,
        [channelTypes.types.webhook]: <Modal title={text.update_webhook} closeModal={() => setModalData({})}
            isOpen={modalData?.status}>
            <UpdateWebhook data={modalData} closePopup={closeUpdateWebhookPopup} webhooks={personalChannels} teamId={teamId}/>
        </Modal>
    }

    const getNotificationChannelCount = key => sortedData?.filter(item => item?.type === key)?.length

    const removeQueryParams = () => {
        const currentUrl = new URL(window.location.href)
        currentUrl.search = ''
        window.history.replaceState({}, '', currentUrl.toString())
    }

    const createSlackChannel = async() => {
        const code = new URLSearchParams(location.search).get('code')
        if (code) {
            await dispatch(addChannel(true, teamId, {
                type: channelTypes.types.slack,
                value: code
            }, null, () => {
            }))
            removeQueryParams()
        }
    }

    const createDiscordChannel = async() => {
        const code = new URLSearchParams(location.search).get('code')
        if (code) {
            await dispatch(addChannel(true, teamId, {
                type: channelTypes.types.discord,
                value: code
            }, null, () => {
            }))
            removeQueryParams()
        }
    }

    const closeCodeModal = async() => {
        setModalData({})
        await dispatch(getPersonalNotificationChannels(isTeamChannel, false, customerIdString))
    }

    const closeTeamChannelPopup = () => {
        dispatch(getPersonalNotificationChannels(true, false))
        handleWebhookChannelButtonOnclick()
        if (type === channelTypes.types.telegram) return
        setModalData({})
        setType(null)
    }

    useEffect(() => {
        dispatch(getAllLimitUsages())
        const newTests = getPreviousTestAlerts().filter(item => new Date(item?.date)?.getDate() === new Date().getDate())
        LocalStorageManager.set(localStorageKeys.tests, newTests)
    }, [])

    useEffect(() => {
        const channelType = new URLSearchParams(location.search).get('channel_type')
        if (location.search) {
            channelType === channelTypes.types.discord ? createDiscordChannel() : createSlackChannel()
        }
    }, [location])

    useEffect(() => {
        dispatch(setCheckedChannels([]))
        setCheckedTypes([])
        setModalData({})
        dispatch(setPersonalChannels([]))
    }, [isTeamChannel])

    useEffect(() => {
        dispatch(setCheckedChannels([]))
        dispatch(setPersonalChannels([]))

        if (!isTeamChannel && members) {
            dispatch(setIsFetching(true))
        }

        if (isTeamChannel) {
            dispatch(getPersonalNotificationChannels(isTeamChannel, true))
            return
        }
    }, [teamId, isTeamChannel])

    useEffect(() => {
        if (!members && !isTeamChannel) {
            dispatch(setIsFetching(false))
        }
    }, [members, isTeamChannel])

    useEffect(() => {
        dispatch(setCheckedChannels([]))
        dispatch(setPersonalChannels([]))
        if (!isTeamChannel && customerIdString) {
            Customer.isRoot() ? members?.length > 0 && dispatch(getPersonalNotificationChannels(false, true, customerIdString)) : dispatch(getPersonalNotificationChannels(false, true, customerIdString))
        }
    }, [customerId?.label, isTeamChannel])

    useEffect(() => {
        setIsDeleteable(!(checkedTypes.some(item => nonDeleteableTypes.includes(item.split('.')[0]))))
    }, [checkedTypes])

    useEffect(() => {
        if (!modalData?.status) {
            setClickedChannel(null)
        }
    }, [modalData])

    useEffect(() => {
        const sorted = [...personalChannels].sort((a, b) => a.priority > b.priority ? 1 : -1)
        setSortedData(sorted)
    }, [personalChannels])

    return (
        <main className={classNames('main channels', {
            dark: isDarkMode
        })}>
            <Confirm
                body={text.are_you_sure}
                callback={deleteChannel}
                showLoader={isDeleting}
            />
            {updateChannelPopups[clickedChannel?.type || checkedChannel?.type]}
            <Modal title={text.enter_code} closeModal={closeCodeModal}
                isOpen={modalData?.status && modalData?.codeModal}>
                <CodePopup data={modalData} setModalData={setModalData} customerId={customerIdString} isTeamChannel={isTeamChannel}/>
            </Modal>
            <Modal title={text.add_new_channel} closeModal={handleWebhookChannelButtonOnclick}
                contentClassName="add-channel-modal"
                isOpen={webhookChannelPopupIsActive}>
                <AddTeamChannel
                    type={type}
                    setType={setType}
                    closePopup={closeTeamChannelPopup}
                    channels={personalChannels}
                    teamId={teamId}
                    setModalData={setModalData}
                />
            </Modal>
            <Modal title={text.add_new_channel} closeModal={handleNewChannelButtonOnclick}
                contentClassName="add-channel-modal"
                isOpen={newChannelPopupIsActive}>
                <AddNotificationChannel
                    setModalData={setModalData}
                    checkVerify={(value) => checkVerify(value)}
                    getNotificationChannelCount={getNotificationChannelCount}
                    closeModal={handleNewChannelButtonOnclick}
                    customerId={customerIdString}
                />
            </Modal>
            <div>
                <ResponsivePageTitle/>
                <div className="content-title">
                    <div className="left-side">
                        <div>
                            <PageSubTitle>
                                <p className={classNames('section', {
                                    dark: isDarkMode
                                })}>{isTeamChannel ? text.team_section : text.personal_section}</p>
                            </PageSubTitle>
                            <PageTitle>
                                <h1  className={classNames('page-title', {
                                    dark: isDarkMode
                                })}>{text.title}</h1>
                            </PageTitle>
                        </div>
                    </div>
                </div>
                <div className="d-flex flex-column gap-half">
                    {Customer.isRoot() && (
                        <>
                            <TeamSwitcher/>
                            {!isTeamChannel &&
                                <CustomerSwitcher value={customerId} onChange={setCustomerId}
                                />}
                        </>
                    )}
                    {Customer.isManager() && !isTeamChannel && (
                        <CustomerSwitcher value={customerId} onChange={setCustomerId} teamId={teamId}/>
                    )}
                </div>
                {
                    isFetching ? <ContentLoader/> : (
                        <>
                            {generateChannels()}
                            {
                                !isTeamChannel && (
                                    <InfoBox description={generateMobileAppLinks()}>
                                        {text.mobile_apps}
                                    </InfoBox>
                                )
                            }
                        </>
                    )
                }
            </div>
            <Footer/>
        </main>
    )
}

export default NotificationChannels
